import { useQuery } from "@tanstack/react-query"
import { createFileRoute, Link } from "@tanstack/react-router"
import { ChevronLeft, Dumbbell } from "lucide-react"
import { useMemo } from "react"
import { Badge } from "../components/ui/badge.tsx"
import { Button } from "../components/ui/button.tsx"
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card.tsx"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table.tsx"
import { apiFetch } from "../utils/apiFetch.ts"
import { generateSmolovJrProgram, type SmolovJrSession } from "../utils/smolovJr.ts"
import { type TProgram, type TWorkoutTemplate } from "../utils/workoutUtils.ts"

export const Route = createFileRoute("/_app/program/$id")({
  component: ProgramDetails,
})

interface SmolovJrMeta {
  type: "smolov-jr"
  oneRepMax: number
  weeklyIncrement: number
  exerciseName: string
}

function parseProgramMeta(notes: string | null): SmolovJrMeta | null {
  if (!notes) {
    return null
  }
  try {
    const meta = JSON.parse(notes)
    if (meta.type === "smolov-jr") {
      return meta as SmolovJrMeta
    }
  } catch {
    // Not valid JSON
  }
  return null
}

function ProgramDetails() {
  const { id } = Route.useParams()

  const { data: program, isLoading } = useQuery({
    queryKey: ["programs", id],
    queryFn: () => apiFetch<TProgram>(`/v1/programs/${id}`),
  })

  const smolovJrMeta = useMemo(() => parseProgramMeta(program?.notes ?? null), [program?.notes])

  if (isLoading) {
    return <div>Loading program details...</div>
  }

  if (!program) {
    return <div>Program not found</div>
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Button variant="ghost" asChild>
          <Link to="/programs">
            <ChevronLeft className="mr-2 h-4 w-4" />
            Back to Programs
          </Link>
        </Button>
        <h1 className="font-bold text-2xl">{program.name}</h1>
        <div></div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Overview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="rounded-md border p-4">
              <h3 className="font-medium">Duration</h3>
              <p>
                {program.durationWeeks} week{program.durationWeeks > 1 ? "s" : ""}
              </p>
            </div>
            <div className="rounded-md border p-4">
              <h3 className="font-medium">Status</h3>
              <Badge variant={program.status === "active" ? "default" : "outline"} className="capitalize">
                {program.status}
              </Badge>
            </div>
            <div className="rounded-md border p-4">
              <h3 className="font-medium">Workouts</h3>
              <p>
                {program.workoutTemplates.length} template{program.workoutTemplates.length !== 1 ? "s" : ""} per week
              </p>
            </div>
          </div>

          {smolovJrMeta && (
            <div className="mt-4 rounded-md border border-dashed p-4">
              <h3 className="mb-2 font-medium">Smolov JR Settings</h3>
              <div className="grid grid-cols-2 gap-4 text-sm md:grid-cols-3">
                <div>
                  <span className="text-muted-foreground">Exercise: </span>
                  <span className="font-medium">{smolovJrMeta.exerciseName}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">1RM: </span>
                  <span className="font-medium">{smolovJrMeta.oneRepMax} lbs</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Weekly Increment: </span>
                  <span className="font-medium">+{smolovJrMeta.weeklyIncrement} lbs</span>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {smolovJrMeta ? <SmolovJrPlan meta={smolovJrMeta} program={program} /> : <GenericProgramPlan program={program} />}
    </div>
  )
}

function SmolovJrPlan({ meta, program }: { meta: SmolovJrMeta; program: TProgram }) {
  const programData = useMemo(
    () => generateSmolovJrProgram(meta.oneRepMax, meta.weeklyIncrement),
    [meta.oneRepMax, meta.weeklyIncrement]
  )

  const week1 = programData.sessions.filter((s) => s.week === 1)
  const week2 = programData.sessions.filter((s) => s.week === 2)
  const week3 = programData.sessions.filter((s) => s.week === 3)

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Dumbbell className="h-5 w-5" />
          Workout Plan
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <SmolovJrWeekTable week={1} sessions={week1} program={program} />
        <SmolovJrWeekTable week={2} sessions={week2} program={program} />
        <SmolovJrWeekTable week={3} sessions={week3} program={program} />

        <div className="rounded-md border border-dashed p-4 text-center">
          <p className="font-medium text-sm">Week 4 — Deload</p>
          <p className="text-muted-foreground text-sm">Rest and recover. Then test your new 1RM.</p>
        </div>
      </CardContent>
    </Card>
  )
}

function SmolovJrWeekTable({
  week,
  sessions,
  program,
}: {
  week: number
  sessions: SmolovJrSession[]
  program: TProgram
}) {
  if (sessions.length === 0) {
    return null
  }

  const isCurrentWeek = program.status === "active" && program.currentWeek === week

  return (
    <div>
      <h3 className="mb-2 font-semibold text-sm">
        Week {week}
        {isCurrentWeek && (
          <Badge className="ml-2" variant="default">
            Current
          </Badge>
        )}
      </h3>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Day</TableHead>
            <TableHead>Sets × Reps</TableHead>
            <TableHead>% 1RM</TableHead>
            <TableHead>Rest</TableHead>
            <TableHead className="text-right">Weight</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sessions.map((session) => (
            <TableRow key={`${session.week}-${session.dayIndex}`}>
              <TableCell className="font-medium">{session.day}</TableCell>
              <TableCell>
                {session.sets} × {session.reps}
              </TableCell>
              <TableCell>{session.percentage * 100}%</TableCell>
              <TableCell>{session.restMinutes} min</TableCell>
              <TableCell className="text-right font-mono">{session.weight} lbs</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

function GenericProgramPlan({ program }: { program: TProgram }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Dumbbell className="h-5 w-5" />
          Workout Plan
        </CardTitle>
      </CardHeader>
      <CardContent>
        {program.workoutTemplates.length === 0 ? (
          <p className="text-muted-foreground">No workout templates scheduled</p>
        ) : (
          <div className="space-y-4">
            {program.workoutTemplates.map((wt) => (
              <TemplateCard key={wt.template.workoutTemplateId} template={wt.template} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function TemplateCard({ template }: { template: TWorkoutTemplate }) {
  const exercises = template.jsonBlob?.exercises ?? []

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{template.name}</CardTitle>
        {template.notes && <p className="text-muted-foreground text-sm">{template.notes}</p>}
      </CardHeader>
      <CardContent>
        {exercises.length === 0 ? (
          <p className="text-muted-foreground text-sm">No exercises defined</p>
        ) : (
          <div className="space-y-3">
            {exercises.map(
              (
                exercise: { name: string; sets: Array<{ weight: number; reps: number; order: number }> },
                exIdx: number
              ) => (
                <div key={exIdx}>
                  <p className="mb-1 font-medium text-sm">{exercise.name}</p>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-16">Set</TableHead>
                        <TableHead>Weight</TableHead>
                        <TableHead>Reps</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {exercise.sets.map((set, setIdx) => (
                        <TableRow key={setIdx}>
                          <TableCell className="font-medium">{set.order || setIdx + 1}</TableCell>
                          <TableCell className="font-mono">{set.weight} lbs</TableCell>
                          <TableCell>{set.reps}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
